'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { hasAudioConsent } from '@/components/consent-banner';

/* ─────────────────────────────────────────────────────────
 * useAutoplayUnlock
 * ─────────────────────────────────────────────────────────
 * Mehrstufige Strategie basierend auf:
 *  - MDN Autoplay Guide (Navigator.getAutoplayPolicy)
 *  - Chrome Autoplay Policy (MEI, PWA, User-Gesture)
 *  - V1 ConsentBanner AudioContext-Trick
 *  - Expliziter User-Consent (ConsentBanner)
 *
 * Stufe 0: Consent prüfen (localStorage/Cookie)
 *   → Ohne Consent wird Audio NICHT entsperrt
 *   → Chrome blockiert Third-Party-Cookies → First-Party nötig
 *
 * Stufe 1: Navigator.getAutoplayPolicy() (Chrome 82+, Firefox)
 *   → Wenn "allowed" → sofort freigeschaltet (PWA, hoher MEI)
 *   → Wenn "allowed-muted" → warte auf User-Interaktion
 *
 * Stufe 2: Globale User-Interaction-Listener (click/touch/key)
 *   → Erste Interaktion + Consent vorhanden → AudioContext-Trick
 *
 * Stufe 3: robustUnmute() (Safari/iOS Fallback)
 *   → Multi-Technik Unmute mit automatischem Fallback
 * ───────────────────────────────────────────────────────── */

// ── TypeScript: Navigator.getAutoplayPolicy existiert noch nicht in lib.dom ──
declare global {
  interface Navigator {
    getAutoplayPolicy?: (type: string | HTMLMediaElement) => 'allowed' | 'allowed-muted' | 'disallowed';
  }
}

// ── Globaler Singleton-State (außerhalb React) ──
let audioUnlocked = false;
const subscribers = new Set<() => void>();

function subscribe(cb: () => void) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

function getSnapshot() {
  return audioUnlocked;
}

function getServerSnapshot() {
  return false;
}

function notifyAll() {
  subscribers.forEach((cb) => cb());
}

function markUnlocked() {
  if (audioUnlocked) return;
  audioUnlocked = true;
  (window as any).__audioUnlocked = true;
  notifyAll();
}

// ── Stufe 1: Navigator.getAutoplayPolicy() ──
// Verfügbar in Chrome 82+, Firefox — NICHT Safari.
// Erkennt ob der Browser unmuted Autoplay bereits erlaubt
// (PWA installiert, hoher Media Engagement Index, etc.)
function checkAutoplayPolicy(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.getAutoplayPolicy) return false;

  try {
    const policy = navigator.getAutoplayPolicy('mediaelement');
    if (policy === 'allowed') {
      markUnlocked();
      return true;
    }
    // 'allowed-muted' oder 'disallowed' → User-Interaktion nötig
    return false;
  } catch {
    return false;
  }
}

// ── PWA Standalone-Mode Erkennung ──
// Installierte PWAs (standalone) haben gelockerte Autoplay-Policies.
// Wenn die App im standalone-Modus läuft, können Videos mit Sound
// ohne User-Interaktion abgespielt werden (browser-unabhängig).
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;

  // 1. display-mode: standalone (W3C Standard)
  const standaloneMedia = window.matchMedia('(display-mode: standalone)');
  if (standaloneMedia.matches) return true;

  // 2. iOS Safari "standalone" property
  if ((navigator as any).standalone === true) return true;

  // 3. TWA (Trusted Web Activity) auf Android
  if (document.referrer.includes('android-app://')) return true;

  // 4. window-controls-overlay (Desktop PWA mit Overlay)
  const wcoMedia = window.matchMedia('(display-mode: window-controls-overlay)');
  if (wcoMedia.matches) return true;

  return false;
}

// ── Stufe 2: AudioContext Silent-Buffer-Trick (V1-Technik) ──
async function unlockAudioContext() {
  try {
    const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioCtx) {
      markUnlocked(); // Kein AudioContext → Fallback, User hat interagiert
      return;
    }

    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // Spiele leeren Buffer → Browser markiert Audio als "erlaubt"
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);

    markUnlocked();

    // AudioContext aufräumen
    src.onended = () => {
      src.disconnect();
      ctx.close().catch(() => {});
    };
  } catch {
    // Fallback: User hat interagiert, Browser sollte Autoplay erlauben
    markUnlocked();
  }
}

// ── Globale Listener-Registrierung (Singleton) ──
let initialized = false;

function initialize() {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  initialized = true;

  // Stufe 0: Consent prüfen — ohne Consent kein Unlock
  // Aber: getAutoplayPolicy 'allowed' (PWA) übersteuert Consent
  if (checkAutoplayPolicy()) return; // PWA/MEI → sofort frei!

  // Stufe 0b: PWA Standalone-Mode → Audio sofort freischalten
  // Installierte PWAs haben gelockerte Autoplay-Richtlinien.
  // Der Browser erlaubt unmuted Autoplay im standalone-Modus.
  if (isStandaloneMode()) {
    // Im Standalone-Modus direkt über AudioContext freischalten
    unlockAudioContext();
    return;
  }

  // Auf ConsentBanner 'sound-allowed' Event reagieren
  document.addEventListener('sound-allowed', () => {
    if (audioUnlocked) return;
    unlockAudioContext();
  });

  // Stufe 2: Warte auf erste User-Interaktion + Consent
  const events = ['click', 'touchstart', 'keydown'] as const;

  function handleInteraction() {
    if (audioUnlocked) return;

    // Consent prüfen — ohne Consent nicht entsperren
    if (!hasAudioConsent()) return;

    // Listener entfernen — wird nur einmal gebraucht
    events.forEach((evt) =>
      document.removeEventListener(evt, handleInteraction, true),
    );

    // Nochmal Policy prüfen (könnte sich durch Interaktion geändert haben)
    if (checkAutoplayPolicy()) return;

    // AudioContext-Trick als Fallback
    unlockAudioContext();
  }

  events.forEach((evt) =>
    document.addEventListener(evt, handleInteraction, {
      capture: true,
      passive: true,
    }),
  );
}

// ── Stufe 3: Robust Unmute (V1-Technik für Safari/iOS) ──
/**
 * Versucht ein HTML-Medienelement robust zu unmuten.
 * Nutzt mehrere Techniken für beste Browser-Kompatibilität:
 * - defaultMuted, muted, removeAttribute('muted')
 * - volume=1, playsInline
 * - Fallback: bei Fehler → muted weiterspielen
 */
export async function robustUnmute(el: HTMLMediaElement): Promise<boolean> {
  // Aggressiv unmuten — Browser darf blockieren, dann Fallback zu muted.
  // Per-Element getAutoplayPolicy wird NICHT geprüft, da es nach
  // User-Interaktion oft noch 'allowed-muted' zurückgibt obwohl
  // unmuted Playback funktionieren würde.
  try {
    el.defaultMuted = false;
    el.muted = false;
    el.removeAttribute?.('muted');
    el.volume = 1;
    (el as any).playsInline = true;
    await el.play?.();
    return !el.muted; // Bestätigung: wirklich unmuted?
  } catch {
    // Browser blockiert → muted weiterspielen
    el.muted = true;
    el.play().catch(() => {});
    return false;
  }
}

// ── React Hook ──
/**
 * Gibt `true` zurück sobald Audio-Autoplay erlaubt ist.
 *
 * Erkennung in 3 Stufen:
 * 0. Consent prüfen (ConsentBanner) → ohne Consent kein Audio
 * 1. Navigator.getAutoplayPolicy() → sofort wenn PWA/MEI
 * 2. User-Interaktion (click/touch/key) + Consent → AudioContext-Trick
 *
 * Verwendung:
 * ```tsx
 * const isAudioUnlocked = useAutoplayUnlock();
 * // im Hover-Handler:
 * if (isAudioUnlocked) await robustUnmute(videoElement);
 * ```
 */
export function useAutoplayUnlock(): boolean {
  useEffect(() => {
    initialize();
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
